// client/src/utils/engagement.js — first-party website engagement for hot-lead alerts.
//
// One anonymous id in a first-party cookie (brec_vid, 1 year, this site only) and a few
// coarse events posted to our own server (/api/public/engagement/event). No third-party
// tracker, no fingerprinting, nothing personal in the event. Nothing at all happens when
// the browser sends Do-Not-Track or Global Privacy Control — the cookie is not even set.
// The server repeats both checks (lib/hotLeads.trackingAllowed).
import { useEffect, useRef } from 'react';

const COOKIE = 'brec_vid';

export const trackingAllowed = () => {
    try {
        if (navigator.globalPrivacyControl === true) return false;
        const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
        return !(dnt === '1' || dnt === 'yes');
    } catch { return false; }
};

const readId = () => {
    const m = document.cookie.match(/(?:^|;\s*)brec_vid=([a-f0-9]{32})(?:;|$)/);
    return m ? m[1] : null;
};

/** The visitor id, created on first use (only when tracking is allowed). */
export const visitorId = () => {
    if (!trackingAllowed()) return null;
    let id = readId();
    if (id) return id;
    try {
        const b = new Uint8Array(16);
        window.crypto.getRandomValues(b);
        id = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${COOKIE}=${id}; Max-Age=${365 * 86400}; Path=/; SameSite=Lax${secure}`;
        return readId();
    } catch { return null; }
};

/** Post one event. Fire-and-forget; never throws, never delays the page. */
export const track = (event_type, listing_key, extra = {}) => {
    if (!listing_key || !visitorId()) return;
    try {
        fetch('/api/public/engagement/event', {
            method: 'POST', keepalive: true, credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_type, listing_key, ...extra }),
        }).catch(() => {});
    } catch { /* ignore */ }
};

/** On a property page: a view on arrival, and coarse time-on-page when the visitor leaves. */
export const useListingEngagement = (listingKey) => {
    const started = useRef(0);
    useEffect(() => {
        if (!listingKey) return undefined;
        started.current = Date.now();
        track('page_view', listingKey);
        let sent = false;
        const leave = () => {
            if (sent) return;
            const seconds = Math.round((Date.now() - started.current) / 1000);
            if (seconds >= 30) { sent = true; track('time_on_page', listingKey, { seconds }); }
        };
        const onHide = () => { if (document.visibilityState === 'hidden') leave(); };
        window.addEventListener('pagehide', leave);
        document.addEventListener('visibilitychange', onHide);
        return () => {
            leave();
            window.removeEventListener('pagehide', leave);
            document.removeEventListener('visibilitychange', onHide);
        };
    }, [listingKey]);
};

export const PRIVACY_LINE = 'This site records which property pages you view, using one first-party cookie and no third-party trackers, '
    + 'so our agents can help you. It is linked to you only if you contact us, chat with us, sign in, or open a link your agent sent you. '
    + 'If your browser sends Do Not Track or Global Privacy Control, nothing is recorded.';
